/// <reference path="../types/perspective-wasm.d.ts" />

/**
 * `<PerspectiveViewer>` — the ito wrapper around FINOS Perspective v5
 * (`@perspective-dev/*`): a pivotable, filterable, chartable data grid for
 * tick-level market data, rendered through the official React bindings.
 *
 * Data flows one way: pass `data` (an array of records) plus an optional
 * typed `config`; the viewer handles pivots, filters, sorts, aggregates and
 * chart plugins internally. Theming follows the design system automatically
 * (`Pro Light` / `Pro Dark`).
 *
 * The wasm engine + plugins register lazily on first mount, so this module
 * stays importable in non-DOM environments (tests, SSR).
 *
 * @example
 * <PerspectiveViewer
 *   data={ticks}
 *   config={{ groupBy: ["symbol"], columns: ["last", "volume"], sort: [["volume", "desc"]] }}
 *   className="h-[480px]"
 * />
 */

import * as React from "react";

// Static asset URLs (side-effect free) for the two wasm binaries.
import SERVER_WASM from "@perspective-dev/server/dist/wasm/perspective-server.wasm?url";
import CLIENT_WASM from "@perspective-dev/viewer/dist/wasm/perspective-viewer.wasm?url";

import { cn } from "../lib/cn";
import { useTheme } from "../theme/use-theme";

/** Perspective sort direction. */
export type PerspectiveSortDir = "asc" | "desc";

/** Row data: an array of records (or any perspective-acceptable payload). */
export type PerspectiveData = Array<object>;

/**
 * Typed view configuration — translated to the viewer's
 * `ViewerConfigUpdate` (snake_case) internally.
 */
export interface PerspectiveViewConfig {
  /** Visible (or pivoted) columns, in order. */
  columns?: Array<string | null>;
  /** Row pivot dimensions. */
  groupBy?: string[];
  /** Column pivot dimensions. */
  splitBy?: string[];
  /**
   * Column aggregates, e.g. `{ last: "avg", volume: "sum" }`. Weighted
   * aggregators take a tuple: `{ price: ["weighted mean", "size"] }`.
   */
  aggregates?: Record<string, string | [string, string[]]>;
  /** Sort spec, e.g. `[["volume", "desc"]]`. */
  sort?: Array<[string, PerspectiveSortDir]>;
  /** Filter spec, e.g. `[["volume", ">", 1_000]]`. */
  filters?: Array<[string, string, unknown]>;
  /**
   * Active plugin. In v5 the datagrid registers as
   * `"perspective-viewer-datagrid"`; chart plugins use names like `"Y Bar"`.
   */
  plugin?: string;
  /** Computed column expressions (Perspective expression syntax). */
  expressions?: string[];
  /** Show the settings sidebar. */
  settings?: boolean;
  /** Theme name override ("Pro Light" | "Pro Dark" | ...). */
  theme?: string;
  /** Free-form plugin config (datagrid styling etc.). */
  pluginConfig?: Record<string, unknown>;
  /** Per-column styling config keyed by column name. */
  columnsConfig?: Record<string, Record<string, unknown>>;
  /** Panel title. */
  title?: string;
}

type ViewerConfigUpdate = Record<string, unknown>;

/** Minimal structural type of the react binding module. */
type ReactBindings = {
  PerspectiveViewer: React.ComponentType<{
    client?: unknown;
    config?: ViewerConfigUpdate;
    onConfigUpdate?: (config: ViewerConfigUpdate) => void;
    className?: string;
    style?: React.CSSProperties;
    hidden?: boolean;
    id?: string;
    slot?: string;
    tabIndex?: number;
    title?: string;
  }>;
};

const THEMES = {
  light: "Pro Light",
  dark: "Pro Dark",
} as const;

/* -------------------------------------------------------------------------- */
/* Lazy wasm/plugin/element bootstrap                                         */
/* -------------------------------------------------------------------------- */

let registryPromise: Promise<ReactBindings> | null = null;

function toRestoreConfig(
  config: PerspectiveViewConfig,
  theme: string,
): ViewerConfigUpdate {
  return {
    ...(config.columns ? { columns: config.columns } : {}),
    ...(config.groupBy ? { group_by: config.groupBy } : {}),
    ...(config.splitBy ? { split_by: config.splitBy } : {}),
    ...(config.aggregates ? { aggregates: config.aggregates } : {}),
    ...(config.sort ? { sort: config.sort } : {}),
    ...(config.filters ? { filter: config.filters } : {}),
    ...(config.plugin ? { plugin: config.plugin } : {}),
    ...(config.expressions ? { expressions: config.expressions } : {}),
    ...(config.settings !== undefined ? { settings: config.settings } : {}),
    ...(config.pluginConfig ? { plugin_config: config.pluginConfig } : {}),
    ...(config.columnsConfig ? { columns_config: config.columnsConfig } : {}),
    ...(config.title !== undefined ? { title: config.title } : {}),
    theme,
  };
}

/**
 * Initialize the Perspective wasm engine, register the
 * `<perspective-viewer>` element and plugins, and return the official React
 * bindings. Idempotent; safe to call from any mount.
 */
export function ensurePerspectiveLoaded(): Promise<ReactBindings> {
  if (!registryPromise) {
    registryPromise = (async () => {
      // Theme stylesheets first so the element adopts them on upgrade.
      await Promise.all([
        import("@perspective-dev/viewer/dist/css/themes.css"),
        import("@perspective-dev/viewer/dist/css/icons.css"),
      ]);
      const [{ default: client }, { default: viewer }, react] =
        await Promise.all([
          import("@perspective-dev/client"),
          import("@perspective-dev/viewer"),
          import("@perspective-dev/react"),
        ]);
      // Plugins register themselves against the element definition.
      await Promise.all([
        import("@perspective-dev/viewer-datagrid"),
        import("@perspective-dev/viewer-charts"),
      ]);
      await Promise.all([
        client.init_server(fetch(SERVER_WASM)),
        viewer.init_client(fetch(CLIENT_WASM)),
      ]);
      return react as unknown as ReactBindings;
    })();
  }
  return registryPromise;
}

/* -------------------------------------------------------------------------- */
/* Table lifecycle (data → named worker table)                                */
/* -------------------------------------------------------------------------- */

let tableCounter = 0;

/** The shared engine worker — created once after bootstrap. */
let workerPromise: Promise<{
  table: (d: unknown, o?: unknown) => Promise<unknown>;
}> | null = null;

async function getWorker() {
  if (!workerPromise) {
    workerPromise = import("@perspective-dev/client").then(
      ({ default: client }) => client.worker() as never,
    );
  }
  return workerPromise;
}

const dispose = (table: unknown): void => {
  (table as { delete?: (o?: unknown) => Promise<void> } | null)
    ?.delete?.({ lazy: true })
    ?.catch?.(() => undefined);
};

async function createTable(data: PerspectiveData): Promise<unknown> {
  const worker = await getWorker();
  const name = `ito_data_${Date.now()}_${tableCounter++}`;
  return worker.table(data, { name });
}

/* -------------------------------------------------------------------------- */
/* React wrapper                                                              */
/* -------------------------------------------------------------------------- */

export interface PerspectiveViewerProps {
  /** Row data to load (replaces the current table). */
  data?: PerspectiveData;
  /** CSS class for sizing (the element fills its container). */
  className?: string;
  style?: React.CSSProperties;
  /** Typed view configuration. */
  config?: PerspectiveViewConfig;
  /** Force a theme name; defaults to the active design-system theme. */
  theme?: string;
  /** Called when the user edits the view config through the viewer UI. */
  onConfigChange?: (config: ViewerConfigUpdate) => void;
  /** Element attributes forwarded manually (not part of viewer config). */
  selectable?: boolean;
  editable?: boolean;
}

/**
 * The ito Perspective data grid / chart surface.
 */
export function PerspectiveViewer({
  data,
  className,
  style,
  config,
  theme,
  onConfigChange,
  selectable,
  editable,
}: PerspectiveViewerProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { resolvedTheme } = useTheme();
  const resolved = theme ?? THEMES[resolvedTheme];
  const [bindings, setBindings] = React.useState<ReactBindings | null>(null);
  const [table, setTable] = React.useState<unknown>(null);
  const [bootError, setBootError] = React.useState<string | null>(null);
  const onConfigChangeRef = React.useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;

  // Bootstrap wasm + element + plugins, then hold the React bindings.
  React.useEffect(() => {
    let mounted = true;
    ensurePerspectiveLoaded()
      .then((b) => {
        if (mounted) setBindings(b);
      })
      .catch((err) => {
        console.error("perspective bootstrap failed", err);
        if (mounted) setBootError(String(err));
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Load data into a named worker table (replacing any previous one).
  const tableRef = React.useRef<unknown>(null);
  React.useEffect(() => {
    if (!bindings) return;
    if (!data) {
      // `data` removed: drop the table state too, or the viewer keeps a
      // reference to a disposed table and renders stale rows.
      const old = tableRef.current;
      tableRef.current = null;
      setTable(null);
      if (old) dispose(old);
      return;
    }
    let disposed = false;
    let mine: unknown = null;
    createTable(data)
      .then((t) => {
        if (disposed) {
          dispose(t);
          return;
        }
        mine = t;
        const old = tableRef.current;
        tableRef.current = t;
        setTable(t);
        dispose(old);
      })
      .catch((err) => console.error("perspective table failed", err));
    return () => {
      disposed = true;
      if (mine) dispose(mine);
      if (tableRef.current === mine) tableRef.current = null;
    };
  }, [bindings, data]);

  // Forward element-level attributes the React bindings don't handle.
  React.useEffect(() => {
    const el = containerRef.current?.querySelector("perspective-viewer");
    if (!el) return;
    if (selectable) el.setAttribute("selectable", "");
    else el.removeAttribute("selectable");
    if (editable) el.setAttribute("editable", "");
    else el.removeAttribute("editable");
  }, [bindings, selectable, editable, table]);

  if (bootError) {
    return (
      <div
        className={cn(
          "flex min-h-[240px] items-center justify-center rounded-lg p-md text-sm text-status-error",
          className,
        )}
        style={{
          border: "1px solid var(--border)",
          background: "var(--card)",
          ...style,
        }}
      >
        Perspective failed to load — {bootError}
      </div>
    );
  }

  const ReactViewer = bindings?.PerspectiveViewer;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative min-h-[240px] min-w-0 overflow-hidden rounded-lg",
        className,
      )}
      style={{
        border: "1px solid var(--border)",
        background: "var(--card)",
        ...style,
      }}
    >
      {ReactViewer && (
        <ReactViewer
          client={table}
          config={toRestoreConfig(config ?? {}, resolved)}
          onConfigUpdate={(c) => onConfigChangeRef.current?.(c)}
          className="absolute inset-0"
          style={{ width: "100%", height: "100%" }}
        />
      )}
    </div>
  );
}

/** Convenience preset: the datagrid plugin, selectable rows. */
export function PerspectiveDataGrid(
  props: Omit<PerspectiveViewerProps, "config"> & {
    config?: Omit<PerspectiveViewConfig, "plugin">;
  },
) {
  return (
    <PerspectiveViewer
      {...props}
      config={{
        plugin: "perspective-viewer-datagrid",
        ...(props.config ?? {}),
      }}
    />
  );
}
